import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { isAtsEmbedActive } from '@/hoc/api/ats-embed';
import ReportSkeleton from './report/ReportSkeleton';
import { SetupScreenSkeleton } from './setup-screen-skeleton';
import { Skeleton, SkeletonCircle, SkeletonRegion } from './skeleton';
/**
 * The app-wide pre-content loading state, shared by the route-chunk Suspense
 * fallback, the App auth gate and the session-renewal hold, so a page that goes
 * through all three looks like one continuous load.
 *
 * It used to be a centred brand ring on every route. Now it draws the layout the
 * route is about to show: the recruiter dashboard chrome (sidebar + top bar) for
 * pages inside DashboardShell — with the report's own skeleton on a report link —
 * and the setup screen on a candidate interview link. Routes whose layout it
 * can't know ahead (marketing, auth, profile pages) get the plain page backdrop:
 * no spinner, and nothing that would be wrong when the page arrives.
 *
 * The route is read from `window.location`, not the router, because the Suspense
 * fallback can render before any route matches.
 *
 * The theme is read straight from localStorage rather than from `useDashTheme`:
 * a hard-coded white backdrop flashed a full-screen white panel on a dark
 * session. Same storage key as useDashTheme, and it listens to the same event so
 * a toggle mid-load repaints. The backdrop colour is also set inline so it is
 * right even before the stylesheet applies.
 */
const STORAGE_KEY = 'dashboard_theme';
const BACKDROP = { light: '#e4e9f0', dark: '#0a0c10' };
const readTheme = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'dark' || saved === 'light')
            return saved;
    }
    catch (_a) {
        /* storage unavailable — fall through to default */
    }
    return 'light';
};
const inFrame = () => {
    try {
        return window.self !== window.top;
    }
    catch (_a) {
        return true; // cross-origin frame access throws → we are embedded
    }
};
// Keep in step with App.tsx's `usesDashShell` (minus /admin, which draws its
// own console chrome) and the candidate routes in index.dev.tsx.
const layoutFor = (path) => {
    // Framed by a host app, the report is drawn without DashboardShell (see
    // ai-interview-evaluation-widget); other framed pages have host-owned chrome.
    if (path.startsWith('/interview-report'))
        return inFrame() && !isAtsEmbedActive() ? 'bare-report' : 'report';
    if (path === '/dashboard' ||
        path.startsWith('/job-description') ||
        path.startsWith('/schedule-interview'))
        return 'dashboard';
    if (path.startsWith('/interview/'))
        return 'candidate';
    return 'plain';
};
// Sidebar groups as rendered by dashboard/Sidebar: Overview (2) · Workspace (2).
const NAV_GROUPS = [
    [72, 80],
    [92, 48],
];
const SidebarSkeleton = () => (_jsxs("div", { className: 'hidden aiMd:flex flex-col flex-shrink-0 h-full w-64', children: [_jsxs("div", { className: 'flex items-center gap-2.5 px-4 py-3.5 aiMd:pt-4', children: [_jsx("div", { className: 'flex-1 h-9 flex items-center', children: _jsx(Skeleton, { className: 'h-5 w-24 rounded' }) }), _jsx(Skeleton, { className: 'w-7 h-7 rounded-md' })] }), _jsx("div", { className: 'flex-1 py-3 px-3', children: NAV_GROUPS.map((items, gi) => (_jsxs("div", { className: gi > 0 ? 'mt-4' : '', children: [_jsx(Skeleton, { className: 'mx-2.5 mb-2 h-2.5 w-16 rounded' }), _jsx("div", { className: 'space-y-0.5', children: items.map((w) => (_jsxs("div", { className: 'flex items-center gap-2.5 px-2.5 py-2', children: [_jsx(Skeleton, { className: 'w-[18px] h-[18px] rounded' }), _jsx(Skeleton, { className: 'h-3 rounded', style: { width: w } })] }, w))) })] }, gi))) }), _jsx("div", { className: 'px-3 py-3', children: _jsxs("div", { className: 'flex items-center gap-2.5 px-2 py-1.5', children: [_jsx(SkeletonCircle, { size: 32 }), _jsxs("div", { className: 'flex-1 min-w-0', children: [_jsx(Skeleton, { className: 'h-3 w-24 rounded' }), _jsx(Skeleton, { className: 'mt-1.5 h-2.5 w-16 rounded' })] })] }) })] }));
const TopBarSkeleton = ({ embedded }) => (_jsxs("div", { className: 'flex-shrink-0 px-3 aiMd:px-5 py-2 aiMd:py-3 flex items-center gap-2 aiMd:gap-3 max-aiSm:gap-1.5 border-b border-transparent', children: [!embedded && (_jsx(Skeleton, { className: 'aiMd:hidden w-8 h-8 max-aiMd:w-11 max-aiMd:h-11 rounded-lg' })), _jsxs("div", { className: 'flex-1 min-w-0', children: [_jsx(Skeleton, { className: 'h-3.5 w-56 max-w-full rounded' }), _jsx(Skeleton, { className: 'mt-1.5 h-3 w-36 rounded max-aiSm:hidden' })] }), _jsx(Skeleton, { className: 'h-8 w-[150px] rounded-lg max-aiMd:h-11 max-aiMd:w-11' }), _jsx(Skeleton, { className: 'h-8 w-8 rounded-lg max-aiMd:h-11 max-aiMd:w-11' }), _jsx(SkeletonCircle, { size: 32 })] }));
/** DashboardShell's chrome, with the page's content (or its heading) inside. */
const DashboardShellSkeleton = ({ report }) => {
    const embedded = isAtsEmbedActive();
    return (_jsxs("div", { className: `flex h-full w-full overflow-hidden ${embedded ? 'bg-dash-bg' : 'bg-dash-bg2'}`, children: [!embedded && _jsx(SidebarSkeleton, {}), _jsxs("div", { className: `flex-1 flex flex-col overflow-hidden min-w-0 bg-dash-bg ${embedded ? '' : 'dash-shell-panel'}`, children: [_jsx(TopBarSkeleton, { embedded: embedded }), _jsx("div", { className: 'flex-1 min-h-0 overflow-hidden', children: report ? (_jsx(ReportSkeleton, {})) : (_jsxs("div", { className: 'px-4 aiSm:px-8 py-6', children: [_jsx(Skeleton, { className: 'h-6 w-56 rounded-md' }), _jsx(Skeleton, { className: 'mt-2 h-3.5 w-80 max-w-full rounded' })] })) })] })] }));
};
export const FullScreenLoader = () => {
    const [theme, setTheme] = useState(readTheme);
    useEffect(() => {
        const onChange = (e) => {
            const next = e.detail;
            if (next === 'light' || next === 'dark')
                setTheme(next);
        };
        window.addEventListener('dash-theme-change', onChange);
        return () => window.removeEventListener('dash-theme-change', onChange);
    }, []);
    const layout = layoutFor(typeof window === 'undefined' ? '/' : window.location.pathname);
    // Candidate pages are always light and sit on their own backdrop.
    if (layout === 'candidate') {
        return (_jsx("div", { className: 'fixed inset-0 z-50 overflow-auto bg-background', children: _jsx(SetupScreenSkeleton, {}) }));
    }
    const themeClass = `vdash${theme === 'light' ? ' vdash-light' : ''}`;
    if (layout === 'bare-report') {
        // Matches the widget's no-shell branch, which is always light.
        return (_jsx("div", { className: 'vdash vdash-light fixed inset-0 z-50 overflow-auto bg-dash-bg', children: _jsx(ReportSkeleton, {}) }));
    }
    if (layout === 'plain') {
        return (_jsx("div", { role: 'status', "aria-busy": 'true', className: `${themeClass} fixed inset-0 z-50`, style: { background: BACKDROP[theme] }, children: _jsx("span", { className: 'sr-only', children: "Loading" }) }));
    }
    // ReportSkeleton is its own status region; the chrome around it is decorative.
    return (_jsx("div", { className: `${themeClass} fixed inset-0 z-50`, style: { background: BACKDROP[theme] }, children: layout === 'report' ? (_jsx(DashboardShellSkeleton, { report: true })) : (_jsx(SkeletonRegion, { label: 'Loading', className: 'contents', children: _jsx(DashboardShellSkeleton, { report: false }) })) }));
};
export default FullScreenLoader;
